<template>
  <div class="mt-4">
    <Tooltip
      :key="
        t('subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText')
      "
      :tooltip-text="
        t('subscribeToEventUpdatesModal.firstStep.form.dateType.toolTipText')
      "
      :disabled="!tooltipEnabled"
      length="large"
      type="span"
      class="self-center mb-2 block"
    >
      <label for="date-type-select" class="block mb-2 required">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.label') }}
      </label>
    </Tooltip>
    <select
      id="date-type-select"
      v-model="dateType"
      :class="[
        'date-type-select w-full rounded border px-3 py-2 text-sm',
        invalidKeys.includes('dateType') ? 'border-red' : 'border-gray-300',
      ]"
      data-test-id="date-type-select"
      @change="emit('clear-errors', 'dateType')"
    >
      <option value="" disabled style="display: none">
        {{
          t('subscribeToEventUpdatesModal.firstStep.form.dateType.placeholder')
        }}
      </option>
      <option value="PUBLIC">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.public') }}
      </option>
      <option value="PRIVATE">
        {{ t('subscribeToEventUpdatesModal.firstStep.form.dateType.private') }}
      </option>
    </select>
    <span
      v-if="invalidKeys.includes('dateType')"
      data-test-id="date-type-validation-error"
      class="text-red text-sm block mt-1"
    >
      {{ errors?.dateType || t('general.form.validation.required') }}
    </span>
  </div>
</template>

<script setup lang="ts">
import type {
  DateRequestErrors,
  DateRequestInvalidKeys,
} from '~/components/date-request/dateRequestTypes'
import type { EventDateType } from '~/composables/types/event'

interface Props {
  tooltipEnabled?: boolean
  errors?: DateRequestErrors | null
  invalidKeys?: DateRequestInvalidKeys
}

const {
  tooltipEnabled = false,
  errors = {},
  invalidKeys = [],
} = defineProps<Props>()

const modelValue = defineModel<EventDateType | null>({ required: true })

const emit = defineEmits<{
  (e: 'clear-errors', field: string): void
}>()

const { t } = useI18n()

const dateType = computed<EventDateType | ''>({
  get: () => modelValue.value ?? '',
  set: (value) => {
    modelValue.value = value === '' ? null : (value as EventDateType)
  },
})
</script>

<style lang="postcss" scoped>
.date-type-select {
  @apply accent-teal-600;
  @apply rounded-lg h-10;
  &:disabled {
    @apply cursor-not-allowed opacity-60;
  }
}

.required:after {
  content: '*';
  position: relative;
  font-size: inherit;
  @apply text-red;
  padding-left: 0.25rem;
  font-weight: 700;
}
</style>
